
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../src/services/authService';
import { AuthContextType, AuthUser, LoginCredentials } from '../types';
import { setAuthTokens, setUser } from '../../src/lib/storage';
import { UserRoleEnums } from '../utility/userRoles';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: AuthUser }
  | { type: 'AUTH_FAILURE' }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'AUTH_REFRESH'; payload: AuthUser };

const initialState: AuthState = { user: null, isAuthenticated: false, isLoading: true };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true };
    case 'AUTH_SUCCESS':
      return { ...state, user: action.payload, isAuthenticated: true, isLoading: false };
    case 'AUTH_FAILURE':
      return { ...state, user: null, isAuthenticated: false, isLoading: false };
    case 'AUTH_LOGOUT':
      return { ...state, user: null, isAuthenticated: false, isLoading: false };
    case 'AUTH_REFRESH':
      return { ...state, user: action.payload, isAuthenticated: true, isLoading: false };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps { children: ReactNode; }

function saveAuth(user: AuthUser, remember?: boolean) {
  if (user.refreshToken) localStorage.setItem('agentic_refresh_token', user.refreshToken);
  localStorage.setItem('agentic_user_data', JSON.stringify(user));
  
  // Also store tenantId separately for easy access by apiClient
  if (user.tenantId) {
    localStorage.setItem('agentic_tenant_id', user.tenantId);
  }
  // Also check if tenantId is nested in user.user object
  if ((user as any)?.user?.tenantId) {
    localStorage.setItem('agentic_tenant_id', (user as any).user.tenantId);
  }

  const clientId = (user as any)?.clientId || (user as any)?.user?.clientId;
  const clientName = (user as any)?.clientName || (user as any)?.user?.clientName;
  if (clientId) localStorage.setItem('agentic_client_id', String(clientId));
  else localStorage.removeItem('agentic_client_id');
  if (clientName) localStorage.setItem('agentic_client_name', String(clientName));
  else localStorage.removeItem('agentic_client_name');
  
  if (remember) localStorage.setItem('agentic_remember_me', 'true');
}
function clearAuth() {
  localStorage.removeItem('agentic_auth_token');
  localStorage.removeItem('agentic_refresh_token');
  localStorage.removeItem('agentic_user_data');
  localStorage.removeItem('agentic_remember_me');
  localStorage.removeItem('agentic_tenant_id');
  localStorage.removeItem('tenant_id');
  localStorage.removeItem('agentic_client_id');
  localStorage.removeItem('agentic_client_name');
}
function parseStoredUser(): AuthUser | null {
  const raw = localStorage.getItem('agentic_user_data');
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}
function isExpired(user?: AuthUser | null) {
  if (!user?.expiresAt) return false; 
  return new Date(user.expiresAt).getTime() <= Date.now();
}


export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const navigate = useNavigate();

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } catch {
    } finally {
      clearAuth();
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<any> => {
    const refreshed = await authService.refresh();
    saveAuth(refreshed);
    dispatch({ type: 'AUTH_REFRESH', payload: refreshed });
    return refreshed;
  }, []);

  const fetchProfileAndSaveToken=async(fresh?: { token: string; refreshToken?: string })=>{
    let activeUser:any = parseStoredUser();
    // Onboarding hands back a new session whose token carries the just-created tenant. Switch to it
    // before anything else: the old token has no tenant and the gateway refuses it for workspace data.
    if (fresh?.token && activeUser) {
      activeUser = { ...activeUser, token: fresh.token, refreshToken: fresh.refreshToken ?? activeUser.refreshToken };
      setAuthTokens(fresh.token, fresh.refreshToken ?? activeUser.refreshToken);
    }
    try {
      if (isExpired(activeUser)) {
        try {
          activeUser = await refreshToken();
        } catch {
          dispatch({ type: 'AUTH_FAILURE' });
          return;
        }
      }
      if(!activeUser){
        return;
      }
      const profile = await authService.profile();
      // console.log("profile==>",profile)
      const merged: AuthUser = { ...activeUser, ...profile, token: activeUser.token, refreshToken: activeUser.refreshToken };
      saveAuth(merged, localStorage.getItem('agentic_remember_me') === 'true');
      // console.log("merged==>",merged)
      dispatch({ type: 'AUTH_SUCCESS', payload: merged });
    } catch {
      dispatch({ type: 'AUTH_LOGOUT'});
    }
  }


  useEffect(() => {
    (async () => {
      try {
        const stored = parseStoredUser();
        if (!stored) {
          dispatch({ type: 'AUTH_FAILURE' });
          return;
        }

        fetchProfileAndSaveToken();

        // let activeUser = stored;
        // if (isExpired(stored)) {
        //   try {
        //     activeUser = await refreshToken();
        //   } catch {
        //     dispatch({ type: 'AUTH_FAILURE' });
        //     return;
        //   }
        // }

      //   try {
      //     const profile = await authService.profile();
      //     const merged: AuthUser = { ...activeUser, ...profile, token: activeUser.token, refreshToken: activeUser.refreshToken };
      //     saveAuth(merged, localStorage.getItem('agentic_remember_me') === 'true');
      //     dispatch({ type: 'AUTH_SUCCESS', payload: merged });
      //   } catch {
      //     dispatch({ type: 'AUTH_SUCCESS', payload: activeUser });
      //   }
      } catch (e) {
        console.error('Auth init error:', e);
        dispatch({ type: 'AUTH_FAILURE' });
      }
      
    })();
  }, []);

  const setTokenAndRefresh=async(data:any,credentials?: LoginCredentials)=>{
       setUser(data?.user, credentials?.rememberMe);
      setAuthTokens(data?.token,data?.refreshToken)

      console.log("user after login==>",data?.user)

      window.location.href = "/"
  }


  const login = async (credentials: LoginCredentials): Promise<void> => {
    dispatch({ type: 'AUTH_START' });
    try {

      const user = await authService.login(credentials);
      setTokenAndRefresh(user,credentials);
    } catch (error) {
      console.error('Login error:', error);
      dispatch({ type: 'AUTH_FAILURE' });
      throw error; 
    }
  };

  const signup = async (payload: any): Promise<any> => {
    // dispatch({ type: 'AUTH_START' });
    try {
      const user = await authService.signup(payload);
      setUser(user?.user);
      setAuthTokens(user?.token,user?.refreshToken)

      // dispatch({ type: 'AUTH_SUCCESS', payload: user });
      // navigate('/dashboard');
      window.location.href = "/"
      return user;
    } catch (error:any) {
      console.error('Signup error:', error?.details?.message);
      // dispatch({ type: 'AUTH_FAILURE' });
      throw error?.details;
    }
  };

  const forgotPassword = async (payload: { email: string }): Promise<any> => {
    try {
      const res = await authService.forgot(payload);
      return res;
    } catch (error) {
      console.error('Forgot password error:', error);
      // do not change auth state here — just forward the error to the caller
      throw error;
    }
  };



  const contextValue: AuthContextType = {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    login,
    signup,
    forgotPassword,
    logout,
    refreshToken,
    fetchProfileAndSaveToken,
    setTokenAndRefresh
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
